// Global Instructions Rule Applied!
//
// Compact money formatting for mobile list rows. Deals/prospects amounts arrive as
// raw numerics with a currency *id* (no preformatted `*_fmt` twin like companies/funds
// have), so we resolve the symbol from `globalConfigs.currencies` and format compactly
// (e.g. `$12.5M`, `$430K`) to fit a one-line row stat. Pure function so it can run inside
// a descriptor's `toRow` map without hooks — the caller passes the currencies slice.
//
// The compact rule mirrors the SQL `format_values(p_compact=true)` used by the
// companies/funds lists so both paths look identical: thousands carry no decimals
// (`$100K`); millions and above carry exactly one decimal, trailing zero kept (`$3.0M`,
// `$14.0M`, `$2.5B`); negatives render in parens.

import { EMPTY } from './format.ts'

/**
 * Fallback currency symbol when a row's currency id/code can't be resolved (or is null).
 * Mirrors the desktop convention (`getCurrencyById(id)?.symbol || '$'` in the kanban
 * cards) so a missing/blank currency still shows a symbol rather than a bare number —
 * notably fixes prospect commitments, whose `prospects__currency` is frequently null.
 */
const DEFAULT_CURRENCY_SYMBOL = '$'

/** Minimal shape we need off a `globalConfigs.currencies` entry. */
export interface CurrencyLike {
  id: string
  code?: string | null
  symbol?: string | null
}

/**
 * Compact-format a number with an already-resolved currency symbol. Thousands round to
 * whole units with no decimals; millions and above keep exactly one decimal place
 * (trailing zero included) — matching the SQL `format_values` compact path.
 */
function compactWithSymbol(amount: unknown, symbol: string): string {
  if (amount === null || amount === undefined || amount === '') return EMPTY
  const n = typeof amount === 'number' ? amount : Number(amount)
  if (!Number.isFinite(n)) return EMPTY
  const abs = Math.abs(n)
  let body: string
  if (abs >= 1e12) body = `${(abs / 1e12).toFixed(1)}T`
  else if (abs >= 1e9) body = `${(abs / 1e9).toFixed(1)}B`
  else if (abs >= 1e6) body = `${(abs / 1e6).toFixed(1)}M`
  else if (abs >= 1e3) body = `${Math.round(abs / 1e3)}K`
  else body = `${Math.round(abs)}`
  const core = `${symbol}${body}`
  return n < 0 ? `(${core})` : core
}

/**
 * Format `amount` compactly with its currency symbol. Returns the em-dash placeholder
 * when the amount is missing/non-numeric. The symbol is best-effort: an unknown or null
 * currency id falls back to the default symbol (never throws), so a real amount always
 * carries a symbol.
 */
export function compactMoney(
  amount: unknown,
  currencyId: string | null | undefined,
  currencies: CurrencyLike[] | null | undefined
): string {
  if (amount === null || amount === undefined || amount === '') return EMPTY
  const n = typeof amount === 'number' ? amount : Number(amount)
  if (!Number.isFinite(n)) return EMPTY

  const symbol =
    (currencyId && Array.isArray(currencies)
      ? currencies.find((c) => c && c.id === currencyId)?.symbol
      : null) || DEFAULT_CURRENCY_SYMBOL

  return compactWithSymbol(n, symbol)
}

/**
 * Like `compactMoney` but resolves the symbol from a currency *code* (e.g. "USD") rather
 * than an id — used where a row carries `currency_code` (the measures RPCs) instead of an
 * id (e.g. the fund Investments list).
 */
export function compactMoneyByCode(
  amount: unknown,
  code: string | null | undefined,
  currencies: CurrencyLike[] | null | undefined
): string {
  const symbol =
    (code && Array.isArray(currencies) ? currencies.find((c) => c && c.code === code)?.symbol : null) ||
    DEFAULT_CURRENCY_SYMBOL
  return compactWithSymbol(amount, symbol)
}

/** A bound `compactMoney` for a fixed currencies list — handed to a descriptor's `toRow` via ListContext. */
export type MoneyFormatter = (amount: unknown, currencyId: string | null | undefined) => string

export function makeMoneyFormatter(currencies: CurrencyLike[] | null | undefined): MoneyFormatter {
  return (amount, currencyId) => compactMoney(amount, currencyId, currencies)
}
